import React from 'react'
import { Card, Grid, Skeleton, csx, Text, Checkbox } from '@vtex/admin-ui'
import { FormattedMessage } from 'react-intl'

import { useOrderForm } from '../../../../core/requests/checkout'
import { useAssemblyItemsContext } from './context/AssemblyItemsContext'
import { isChecked } from './utils/isChecked'
import { AssemblyItemsFormattedPrice } from './AssemblyItemsFormattedPrice'
import { AssemblyItemsDiscountText } from './AssemblyItemsDiscountText'
import type { AssemblyItemSelectBaseProps } from './AssemblyItemSelect'
import type { PurchaseConditionPrice } from '../Advertisements/types'

export type AssemblyItemSelectCheckboxProps = {
  hasDiscount: boolean
  isLoadingSimulation: boolean
  purchaseConditionPrice: PurchaseConditionPrice | undefined
  currentAssembly: {
    id: string
    name: string
    imageUrl: string
    assemblyId: string
    seller: string
  }
} & AssemblyItemSelectBaseProps

export const AssemblyItemSelectCheckbox = ({
  hasDiscount,
  isLoadingSimulation,
  purchaseConditionPrice,
  minQuantity,
  maxQuantity,
  currentAssembly,
}: AssemblyItemSelectCheckboxProps) => {
  const { assemblyItemState, setAssemblyItemState } = useAssemblyItemsContext()
  const { getCurrencyCode } = useOrderForm()
  const currencyCode = getCurrencyCode()

  const isDisabled = () => {
    return (
      assemblyItemState.values.filter(
        (value) => value.assemblyId === currentAssembly.assemblyId
      ).length === maxQuantity &&
      !isChecked(assemblyItemState.values, currentAssembly)
    )
  }

  const handleCheckAssemblyItem = () => {
    if (!isDisabled()) {
      setAssemblyItemState(currentAssembly, minQuantity, maxQuantity)
    }
  }

  return (
    <Card
      key={currentAssembly.id}
      className={csx({
        display: 'grid',
        padding: '1.375rem',
        rowGap: '2.875rem',
        cursor: 'pointer',
        background: isDisabled() ? '$disabled' : '',
        border: isChecked(assemblyItemState.values, currentAssembly)
          ? '$mainSelected'
          : '$neutral',
        ':hover': {
          background: isDisabled() ? '$disabled' : '$blue05',
        },
      })}
      onClick={handleCheckAssemblyItem}
    >
      <Checkbox
        label=""
        value={currentAssembly.id}
        checked={isChecked(assemblyItemState.values, currentAssembly)}
        disabled={isDisabled()}
      />
      <Grid templateColumns="repeat(2, 1fr)">
        <Text
          className={csx({
            color: isDisabled() ? '#747474 !important' : '',
          })}
        >
          {currentAssembly.name}
        </Text>
        <div className={csx({ textAlign: 'end' })}>
          {isLoadingSimulation ? (
            <Skeleton
              className={csx({
                display: 'inline-block',
                width: '50%',
                height: '1rem',
              })}
            />
          ) : purchaseConditionPrice ? (
            <div
              className={csx({
                color: isDisabled() ? '#747474 !important' : '',
              })}
            >
              {hasDiscount && (
                <AssemblyItemsDiscountText
                  listPrice={purchaseConditionPrice?.listPrice}
                  currencyCode={currencyCode}
                  isDisabled={isDisabled()}
                />
              )}
              <AssemblyItemsFormattedPrice
                value={purchaseConditionPrice?.price}
                currencyCode={currencyCode}
                isDisabled={isDisabled()}
              />
            </div>
          ) : (
            <Text tone="secondary">
              <FormattedMessage id="unavailable" />
            </Text>
          )}
        </div>
      </Grid>
    </Card>
  )
}
